import { assessmentDelayFields } from "./srfAssessmentDelays.js";
const srfPartNumbers = {
    assessmentDetails: 1,
    soadNotes: 6,
};
const assessmentDelayFieldSet = new Set(assessmentDelayFields);
/**
 * Withholds the SRF answers meant for SOAD and CQC eyes only: the SOAD Notes
 * part, and the assessment delay answers recorded in the assessment details
 * part. Providers are sent the report without them because the delay causes
 * can name the provider's own failings.
 *
 * Matches parts by their `part` number, not their position, and returns them
 * in the order given with their other properties intact.
 */
export function withoutCqcOnlySrfAnswers(parts) {
    return parts
        .filter((p) => p.part !== srfPartNumbers.soadNotes)
        .map((p) => p.part === srfPartNumbers.assessmentDetails
        ? { ...p, data: withoutAssessmentDelays(p.data) }
        : p);
}
/**
 * The report form's answers as a certificate carries them: one entry per part,
 * in part order, with the CQC-only answers withheld. Certificates go to
 * providers. The withheld SOAD Notes part stays as an empty entry, so the list
 * keeps the shape of `SoadCertificatePartData`'s `srfData`.
 */
export function srfDataForCertificates(parts) {
    return [...parts]
        .sort((a, b) => a.part - b.part)
        .map((p) => withoutCqcOnlySrfAnswers([p])[0]?.data ?? {});
}
const isSrfPart = (value) => typeof value === "object" &&
    value !== null &&
    "part" in value &&
    typeof value.part === "number" &&
    "data" in value;
/**
 * Narrows parts that carry their part numbers, for `withoutCqcOnlySrfAnswers`.
 * Throws unless every element has a numeric `part` and a `data` property.
 */
export function srfPartsOf(data) {
    if (!Array.isArray(data) || !data.every(isSrfPart)) {
        throw new Error("Expected SOAD report form parts identified by part");
    }
    return data;
}
/**
 * Numbers parts by their position, for `withoutCqcOnlySrfAnswers`. It assumes
 * they start at part 1 with none missing, since a gap would shift every later
 * part's number. Throws unless every element has a `data` property.
 */
export function numberSrfPartsByPosition(data) {
    if (!Array.isArray(data) || !data.every(hasDataProperty)) {
        throw new Error("Expected SOAD report form parts in part order");
    }
    return data.map((p, index) => ({ ...p, part: index + 1 }));
}
const hasDataProperty = (value) => typeof value === "object" && value !== null && "data" in value;
function withoutAssessmentDelays(data) {
    if (typeof data !== "object" || data === null)
        return data;
    return Object.fromEntries(Object.entries(data).filter(([key]) => !assessmentDelayFieldSet.has(key)));
}
//# sourceMappingURL=srfCqcOnlyAnswers.js.map